import React, { useEffect, useState } from 'react'
import { Control, Field, Label,
  TagWrap, Tag, Remove,
  Grid, Cell, PreviewViewport,
  StepFormWrapper,
  ModalOverlay,
  ModalContent,
  CloseIcon,
  ModalBody,
  ModalListItem,
  ModalList,
  ModalHeader,
  LabelWrapper,
  QuestionIcon} from '../../styles/login.style'
import { FaTimes } from 'react-icons/fa'
import {  ProfilePatch} from '../../hooks/auth.service'
import {SUBJECT_OPTIONS, LEVEL_OPTIONS} from '../../types/schemas/abc'
import {TableHint} from '../../components/register/InfoBlock'; // ⬅️ new import
import SubjectCombobox from '../../components/common/SubjectCombobox'


interface Props {
  data: ProfilePatch
  onChange: (d: Props['data']) => void
  onValidityChange: (ok: boolean) => void
  role?: 'tutor' | 'parent'
}

const ExpertiseStep: React.FC<Props> = ({ data, onChange, onValidityChange, role = 'tutor' }) => {
  const { 
    subjects = [], 
    levels = [], 
    expertise = {},
    description = '' 
  } = data


  const [showAllSubjects, setShowAllSubjects] = useState(false)
  const [showAllLevels, setShowAllLevels] = useState(false)

  const addSubject = (s: string) => {
    if (!s) return;
    onChange({ ...data, subjects: [...subjects, s] });
  }

  const addLevel   = (l: string) => {
    if (!l) return;
    onChange({ ...data, levels: [...levels, l] });
  }

  const removeSubject = (s: string) =>
    onChange({ ...data, subjects: subjects.filter(x => x !== s) })
  const removeLevel = (l: string) =>
    onChange({ ...data, levels: levels.filter(x => x !== l) })

  const rowSubjects = subjects.filter(s => !LEVEL_OPTIONS.includes(s))
  const colLevels   = levels.filter(l => !SUBJECT_OPTIONS.includes(l))

  const [map, setMap] = useState<Record<string,string[]>>(expertise)

//   // validity
  const descriptionWordCount = description.trim()
    ? description.trim().split(/\s+/).length
    : 0
  const descOK     = descriptionWordCount <= 500
  const levelsOK   = levels.length > 0
  const subjectsOK = subjects.length > 0
  useEffect(() => {
    onValidityChange(subjectsOK && levelsOK && descOK)
  }, [subjectsOK, levelsOK, descOK, onValidityChange])

  const fieldCss = {
    display: 'flex', flexDirection: 'column', gap: '0.25rem',
    width: '100%', minWidth: 0,
  } as const



const [unticked, setUnticked] = useState<Set<string>>(new Set());

const toggle = (subject: string, level: string) => {
  setUnticked((prev) => {
    const key = `${subject}|||${level}`;
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
};

  // ─── Lift the “ticked” table into data.expertise ────────────────
  useEffect(() => {
    const expertiseMap: Record<string, string[]> = {}
    rowSubjects.forEach(subject => {
      expertiseMap[subject] = colLevels.filter(level => {
        // if it’s NOT in unticked, it’s ticked
        return !unticked.has(`${subject}|||${level}`)
      })
    })

    // push up the new expertise map
    onChange({ ...data, expertise: expertiseMap })
 }, [unticked, subjects.join(','), levels.join(',')])

  return (
    <>
<StepFormWrapper>
<Field name="subjects" css={fieldCss}>
<LabelWrapper>
            <Label>{role === 'parent' ? 'Subjects Needed' : 'Subjects'} (press Enter)</Label>
            <QuestionIcon onClick={() => setShowAllSubjects(true)} />
          </LabelWrapper>
        <SubjectCombobox
          options={SUBJECT_OPTIONS}
          mode="multi"
          exclude={subjects}
          onSelect={addSubject}
          placeholder="Search or select a subject…"
        />

        <TagWrap>
          {subjects.map(s => (
            <Tag key={s} title={s}>
              {s}
              <Remove onClick={() => removeSubject(s)}>
                <FaTimes size={10} />
              </Remove>
            </Tag>
          ))}
        </TagWrap>
      </Field>


      {/* Levels */}
      <Field name="levels" css={fieldCss}>
      <LabelWrapper>
            <Label>{role === 'parent' ? 'Levels Needed' : 'Teaching Levels'} (press Enter)</Label>
            <QuestionIcon onClick={() => setShowAllLevels(true)} />
          </LabelWrapper>

<SubjectCombobox
          options={LEVEL_OPTIONS}
          mode="multi"
          exclude={levels}
          onSelect={addLevel}
          placeholder="e.g. GCSE, A-level"
        />

        <TagWrap>
          {levels.map(s => (
            <Tag key={s} title={s}>
              {s}
              <Remove onClick={() => removeLevel(s)}>
                <FaTimes size={10} />
              </Remove>
            </Tag>
          ))}
        </TagWrap>
      </Field>

      {rowSubjects.length && colLevels.length ? (
<>
<TableHint/>
<PreviewViewport>
  
<Grid
  css={{
    gridTemplateColumns: `minmax(140px,1fr) repeat(${colLevels.length}, minmax(110px,1fr))`,
  }}
>

      {/* blank corner */}            <Cell header />

      {/* column headers = levels */} 
      {colLevels.map((level) => (
        <Cell header key={`c-${level}`}>{level}</Cell>
      ))}

      {/* rows = subjects */} 
      {rowSubjects.map((subject) => (
  <React.Fragment key={`r-${subject}`}>
    <Cell header>{subject}</Cell>

    {colLevels.map((level) => {
      const key = `${subject}|||${level}`;
      const isTicked = !unticked.has(key);

      return (
        <Cell
          key={key}
          ticked={isTicked}
          css={{ cursor: 'pointer' }}
          onClick={() => toggle(subject, level)}
        >
          {isTicked ? '✓' : ''}
        </Cell>
      );
    })}
  </React.Fragment>
))}
    </Grid>
  </PreviewViewport>
  </>
) : null}
      <Field name="description" css={fieldCss}>
  <Label>{role === 'parent' ? 'About You' : 'Short Bio'} (500 words max)</Label>

  <Control
    as="textarea"
    rows={4}
    required
    value={description}
    onChange={e =>
      onChange({ ...data, description: e.currentTarget.value })
    }
    placeholder={role === 'parent'
      ? "Tell tutors a bit about your child and what you're looking for..."
      : "Tell parents a little about yourself and your teaching style..."}
    css={{ backgroundColor: 'white', height: 'auto' }}
  />

  <small style={{ alignSelf: 'flex-end', color: descriptionWordCount > 500 ? '#dc2626' : undefined }}>
    {descriptionWordCount}/500 words
  </small>
</Field>
</StepFormWrapper>

{showAllSubjects && (
        <ModalOverlay onClick={() => setShowAllSubjects(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>All Available Subjects</h3>
              <CloseIcon onClick={() => setShowAllSubjects(false)} />
            </ModalHeader>
            <ModalBody>
              <ModalList>
                {SUBJECT_OPTIONS.map((opt) => (
                  <ModalListItem key={opt}>{opt}</ModalListItem>
                ))}
              </ModalList>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* === ALL LEVELS MODAL === */}
      {showAllLevels && (
        <ModalOverlay onClick={() => setShowAllLevels(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>All Available Levels</h3>
              <CloseIcon onClick={() => setShowAllLevels(false)} />
            </ModalHeader>
            <ModalBody>
              <ModalList>
                {LEVEL_OPTIONS.map((opt) => (
                  <ModalListItem key={opt}>{opt}</ModalListItem>
                ))}
              </ModalList>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

    </>
  )
}

export default ExpertiseStep
